import { useTranslation } from 'react-i18next';
import { Info, ScanFace } from 'lucide-react';
import { FaceShape, type FrameRecommendationAppliedInputs } from '@optiflow/types';
import { Button } from '@/components/ui/Button';
import { FaceShapeGlyph } from '@/modules/face/components/FaceShapeGlyph';

/**
 * What the recommendation knew about the customer's face.
 *
 * Only the confirmed decision is shown — its name, and for a mixed face the two shapes behind it — never a suggestion, a score or a version. Without a usable confirmed shape the screen says so gently and keeps working; recording one is offered to someone allowed to, and never required.
 */
export function FaceShapeContext({
  applied,
  canRecordFaceShape,
  onRecordFaceShape,
}: {
  applied: FrameRecommendationAppliedInputs;
  canRecordFaceShape: boolean;
  onRecordFaceShape: () => void;
}) {
  const { t } = useTranslation();
  const face = applied.face;

  if (applied.style && face) {
    const shape = t(`face.shapes.${face.confirmedShape}`);
    const components =
      face.confirmedShape === FaceShape.MIXED &&
      face.confirmedComponentA &&
      face.confirmedComponentB
        ? t('frameRecommendations.face.components', {
            a: t(`face.shapes.${face.confirmedComponentA}`),
            b: t(`face.shapes.${face.confirmedComponentB}`),
          })
        : null;

    return (
      <div className="border-line flex items-center gap-3 rounded-xl border bg-white px-4 py-3">
        <span className="text-brand-600 shrink-0">
          <FaceShapeGlyph shape={face.confirmedShape} className="h-9 w-7" />
        </span>
        <div className="min-w-0">
          <p className="text-ink text-sm font-medium">
            {t('frameRecommendations.face.confirmed', { shape })}
            {components ? <span className="text-muted font-normal"> · {components}</span> : null}
          </p>
          <p className="text-muted text-xs">{t('frameRecommendations.face.considered')}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      className="border-brand-200 bg-brand-50 text-brand-800 flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 text-sm"
    >
      <Info className="size-4 shrink-0" aria-hidden />
      <p className="min-w-0 flex-1">{t('frameRecommendations.face.missing')}</p>
      {canRecordFaceShape ? (
        <Button size="sm" variant="secondary" onClick={onRecordFaceShape}>
          <ScanFace className="size-4" aria-hidden />
          {t('frameRecommendations.face.record')}
        </Button>
      ) : null}
    </div>
  );
}
